import React, { useState, createContext, useContext, ReactNode, useEffect } from 'react';
import axios from '../config/axiosConfig';
import { useCookies } from 'react-cookie';
import {getMe} from '../api/api'
import {useAuth} from '../app/modules/auth/core/Auth'
import {AuthModel} from '../app/modules/auth/core/_models'

interface IUserRoleContext {
  roles: any[];
  setRoles: (roles: string[]) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (nome: string, cognome: string, email: string, password: string) => Promise<void>;
  isAuthenticating: boolean;
}

const UserRoleContext = createContext<IUserRoleContext | null>(null);

interface UserRoleProviderProps {
  children: ReactNode;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies(['api_token'])
  const [roles, setRoles] = useState<string[]>([])
  const { auth, saveAuth, setCurrentUser } = useAuth()   // <-- PRENDI auth QUI
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  useEffect(() => {
    const checkAuth = async () => {
      setIsAuthenticating(true)
      try {
        // NON serve header qui: AuthProvider imposta già Authorization di default
        const userRolesResponse = await axios.get('/api/auth/user/roles')
        const rolesData = userRolesResponse.data
        setRoles(Array.isArray(rolesData) ? rolesData : [rolesData])

        const user = await getMe()
        setCurrentUser(user)
      } catch (error) {
        console.error("Errore durante il controllo dell'autenticazione", error)

        // hard logout
        removeCookie('api_token', { path: '/' })
        setRoles([])
        saveAuth(undefined)
        setCurrentUser(undefined)

        window.location.href = '/auth'
      } finally {
        setIsAuthenticating(false)
      }
    }

    // ✅ QUESTA è la chiave: se l'app crede di essere loggata (auth in storage),
    // allora verifichi sempre. Se fallisce -> logout.
    if (auth?.api_token) {
      checkAuth()
    } else {
      setRoles([])
      setIsAuthenticating(false)
    }
  }, [auth?.api_token, removeCookie, saveAuth, setCurrentUser])

  const login = async (email: string, password: string) => {
    setIsAuthenticating(true);
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { data } = response;

      if (data.success) {
        setCookie('api_token', data.token, { path: '/', secure: false, httpOnly: false});
        saveAuth({ api_token: data.token });

        /*const [userRolesResponse, user] = await Promise.all([
        axios.get('/api/auth/user/roles', { headers: { Authorization: `Bearer ${data.api_token}` }}),
        getUserByToken(data.api_token)]);*/
		
        const userRolesResponse = await axios.get('/api/auth/user/roles', { headers: { Authorization: `Bearer ${data.token}` }});
        const user = await getMe();

        const rolesData = userRolesResponse.data;
        setRoles(Array.isArray(rolesData) ? rolesData : [rolesData]);
        //const user = await getUserByToken(data.api_token);
        setCurrentUser(user);
      } else {
        throw new Error('Credenziali login non valide');
      }
    } catch (error) {
      console.error('Errore durante il login', error);
    } finally {
      setIsAuthenticating(false); 
    }
  };

  const register = async (nome: string, cognome: string, email: string, password: string) => {
    setIsAuthenticating(true); 
    try {
      const response = await axios.post('/api/auth/register', { nome, cognome, email, password });
      const { data } = response;

      console.log(data.success);

      if (data.success) {
        setCookie('api_token', data.token, { path: '/', secure: false, httpOnly: false});
        const userRolesResponse = await axios.get('/api/auth/user/roles', { headers: { Authorization: `Bearer ${data.token}` }}); //qua l'errore
        const rolesData = userRolesResponse.data;
        setRoles(Array.isArray(rolesData) ? rolesData : [rolesData]);
        const user = await getMe();
        setCurrentUser(user);
      } else {
        throw new Error('Errore durante la registrazione');
      }
    } catch (error) {
      console.error('Errore durante la registrazione', error);
      throw error;
    } finally {
      setIsAuthenticating(false);  
    }
  };

  const logout = async () => {
    // Invia una richiesta al server per invalidare il token
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Errore durante il logout', error);
    }

    // Cancella il cookie dal lato client
    removeCookie('api_token', { path: '/' });
    setRoles([]);
    saveAuth(undefined);
    setCurrentUser(undefined);
  };

  const value = { roles, setRoles, login, logout, register, isAuthenticating};

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
}

export function useRoles() {
  const context = useContext(UserRoleContext);
  if (context === null) {
    throw new Error('useRoles must be used within a UserRoleProvider');
  }
  return context;
}